import React, { useEffect, useState, useCallback } from 'react';
import {
  Container,
  Box,
  Typography,
  IconButton,
  Stack
} from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import axios from 'axios';
import CalendarDay from './CalendarDay';
import MobileEventsList from './MobileEventsList';
import EventDetailsModal from './EventDetailsModal';
import LocationFilter from './LocationFilter';
import RankFilter, { TOURNAMENT_RANKS } from './RankFilter';
import eventsData from './EventsData';
import { styles } from './styles';
import { useTournaments } from '../../services/tournament-service';

function Calendar() {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [filterOptions, setFilterOptions] = useState({
    useLocation: false,
    radius: 100,
    location: null,
    ranks: ['LOCAL', 'REGIONAL', 'NATIONAL', 'FOREIGN', 'INTERNATIONAL'] // Specify default ranks here
  });

  const daysOfWeek = ['Pon', 'Wt', 'Śr', 'Czw', 'Pt', 'Sob', 'Niedz'];

  // const fetchEvents = useCallback(async () => {
  //   try {
  //     const response = await axios.get('https://playlorcana-backend.onrender.com/api/tournaments');
  //     setEvents(response.data);
  //   } catch (error) {
  //     console.error('Błąd podczas pobierania wydarzeń:', error);
  //   }
  // }, []);

  // Przykładowe dane wydarzeń z dodanymi rangami
  //const [events, setEvents] = useState(eventsData);
  const { tournaments: events, loading, error } = useTournaments(filterOptions, currentMonth);
  
  // Funkcja pomocnicza do obliczania odległości między dwoma punktami geograficznymi
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Promień Ziemi w kilometrach
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c; // Odległość w km
  };

  // Filtrowanie wydarzeń na podstawie lokalizacji i rangi
  useEffect(() => {
    let filtered = [...events];

    // Filtrowanie po randze najpierw, aby mieć dostęp do informacji o randze
    if (filterOptions.ranks && filterOptions.ranks.length > 0) {
      filtered = filtered.filter(event => filterOptions.ranks.includes(event.rank));
    }

    // Filtrowanie po lokalizacji, ale z pominięciem wydarzeń o randze NATIONAL, INTERNATIONAL i FOREIGN
    if (filterOptions.useLocation && filterOptions.location) {
      filtered = filtered.filter(event => {
        // Nie filtruj po lokalizacji dla ważniejszych rang - zawsze je pokazuj
        if (['NATIONAL', 'INTERNATIONAL', 'FOREIGN'].includes(event.rank)) {
          return true;
        }

        // Filtruj po lokalizacji tylko dla LOCAL i REGIONAL
        if (['LOCAL', 'REGIONAL'].includes(event.rank)) {
          const tournamentLocation = event.customLocation?.coordinates || event.organizer?.coordinates;
          if (tournamentLocation) {
            const distance = calculateDistance(
              filterOptions.location.lat,
              filterOptions.location.lng,
              tournamentLocation.lat,
              tournamentLocation.lng
            );
            return distance <= filterOptions.radius;
          }
          return false; // Jeśli nie ma koordynatów, nie uwzględniaj w wynikach
        }

        // Dla innych rang (jeśli jakieś są) nie filtruj po lokalizacji
        return true;
      });
    }

    setFilteredEvents(filtered);
  }, [events, filterOptions]);

  const generateCalendarDays = useCallback((year, month) => {
    const firstDayOfMonth = new Date(year, month, 1);
    const lastDayOfMonth = new Date(year, month + 1, 0);
    const daysInMonth = lastDayOfMonth.getDate();
    const startingDay = firstDayOfMonth.getDay() === 0 ? 6 : firstDayOfMonth.getDay() - 1;

    return [
      ...Array(startingDay).fill(null),
      ...Array.from({ length: daysInMonth }, (_, i) => i + 1)
    ];
  }, []);

  const handleEventClick = useCallback((event) => {
    setSelectedEvent(event);
    setModalOpen(true);
  }, []);

  const handlePrevMonth = () => {
    setCurrentMonth(prev => {
      const prevMonth = new Date(prev);
      prevMonth.setMonth(prevMonth.getMonth() - 1);
      return prevMonth;
    });
  };

  const handleNextMonth = () => {
    setCurrentMonth(prev => {
      const nextMonth = new Date(prev);
      nextMonth.setMonth(nextMonth.getMonth() + 1);
      return nextMonth;
    });
  };

  const handleRankFilterChange = (ranks) => {
    setFilterOptions(prev => ({
      ...prev,
      ranks
    }));
  };

  // useEffect(() => {
  //   fetchEvents();
  // }, [fetchEvents]);

  const calendarDays = generateCalendarDays(
    currentMonth.getFullYear(),
    currentMonth.getMonth()
  );

  return (
    <Container sx={styles.calendarContainer}>
      {/* Filtr rangi i lokalizacji */}
      <Box sx={{ mb: 4 }}>
        <LocationFilter onFilterChange={setFilterOptions} />
        <RankFilter
          selectedRanks={filterOptions.ranks}
          onRankChange={handleRankFilterChange}
        />
      </Box>

      {/* Widok desktopowy */}
      <Box sx={styles.desktopView}>
        <Box sx={styles.calendarHeader}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <IconButton onClick={handlePrevMonth} sx={styles.navButton}>
              <ArrowBackIosIcon />
            </IconButton>

            <Typography variant="h5" sx={styles.monthTitle}>
              {currentMonth.toLocaleString('pl-PL', {
                month: 'long',
                year: 'numeric'
              })}
            </Typography>

            <IconButton onClick={handleNextMonth} sx={styles.navButton}>
              <ArrowForwardIosIcon />
            </IconButton>
          </Stack>
        </Box>

        {/* Dni tygodnia */}
        <Box sx={styles.weekDaysContainer}>
          {daysOfWeek.map((day) => (
            <Typography key={day} sx={styles.weekDay}>
              {day}
            </Typography>
          ))}
        </Box>

        {/* Dni miesiąca */}
        <Box sx={styles.daysGrid}>
          {calendarDays.map((day, index) => (
            <CalendarDay
              key={index}
              day={day}
              events={filteredEvents}
              currentMonth={currentMonth}
              onEventClick={handleEventClick}
            />
          ))}
        </Box>
      </Box>

      {/* Widok mobilny */}
      <Box sx={styles.mobileView}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <IconButton onClick={handlePrevMonth}>
            <ArrowBackIosIcon fontSize="small" />
          </IconButton>

          <Typography variant="h5" sx={styles.mobileTitle}>
            {currentMonth.toLocaleString('pl-PL', {
              month: 'long',
              year: 'numeric'
            })}
          </Typography>

          <IconButton onClick={handleNextMonth}>
            <ArrowForwardIosIcon fontSize="small" />
          </IconButton>
        </Box>

        <MobileEventsList
          events={filteredEvents.filter(event => {
            const eventDate = new Date(event.date);
            return eventDate.getMonth() === currentMonth.getMonth() &&
              eventDate.getFullYear() === currentMonth.getFullYear();
          })}
          onEventClick={handleEventClick}
        />
      </Box>

      {/* Modal z szczegółami */}
      <EventDetailsModal
        event={selectedEvent}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        userLocation={filterOptions.useLocation ? filterOptions.location : null}
      />
    </Container>
  );
}

export default Calendar;